







































































































































































































































































































































































































































































































































































































































































































































































































































// 3rd party libraries
import VueDraggable from 'vuedraggable';
import moment from 'moment';
import { get } from 'vuex-pathify';
import { mapActions, mapGetters } from 'vuex';
import { AxiosError } from 'axios';

// utilities
import mixins, { ExtractVue } from '@/utils/mixins';
import {
  IQuote,
  IQuoteCustomerPricing,
  IQuoteProductCategory,
  Quote,
  QuoteExtraItem,
  QuoteFreightItem,
  QuotePaymentTermItem,
  QuoteServiceItem,
  QuoteState,
  QuoteTermItem,
  QuoteType
} from '@/models/quote';
import { Customer } from '@/models/customer';

// mixins
import componentState, { STATE } from '@/mixins/state';
import draggable from '@/mixins/draggable';
import snackbar from '@/mixins/snackbar';
import errorForm from '@/mixins/error-form';

import { Currency, CurrencyRate } from '@/models/currency';
import { cloneDeep } from 'lodash';
import { deepEqual, roundCurrency } from '@/utils/helpers';
import { Module, ModulePaymentTypeEnum } from '@/models/configurator/module';
import { ErrorManager } from '@/models/error';
import { IQuoteServiceTemplate } from '@/models';

import QuoteCustomerSpecialPricingCard from './QuoteCustomerSpecialPricingCard.vue';
import ErrorAlert from '../common/ErrorAlert.vue';
import ConfirmationDialog from '../common/ConfirmationDialog.vue';
import QuoteProductSubscriptionItemBody from './QuoteProductSubscriptionItemBody.vue';
import QuoteProductItemBody from './QuoteProductItemBody.vue';
import QuoteTestBanner from './QuoteTestBanner.vue';

export default mixins(componentState, draggable, snackbar, errorForm).extend({
  components: {
    VueDraggable,
    QuoteCustomerSpecialPricingCard,
    ErrorAlert,
    ConfirmationDialog,
    QuoteProductSubscriptionItemBody,
    QuoteProductItemBody,
    QuoteTestBanner
  },

  data: () => ({
    state: STATE.LOADING,
    showDetails: false,
    originalQuote: null as null | Quote,
    quote: new Quote(),
    equipmentQuoteCategories: [] as IQuoteProductCategory[],
    subscriptionQuoteCategories: [] as IQuoteProductCategory[],

    languages: [
      { title: 'English', locale: 'en' },
      { title: 'Spanish', locale: 'es' },
      { title: 'Russian', locale: 'ru' }
    ],

    rightQuoteDrawer: true,

    errorMessage: '',
    detailErrorMessage: '',
    loading: false,
    discardUnsavedDataDialog: false,
    QuoteState
  }),

  computed: {
    ...mapGetters({
      getQuoteById: 'quote/getQuoteById'
    }),

    getCustomerById: get<(id: string) => Customer>('customer/getCustomerById'),

    getCurrencyById: get<(id: number) => CurrencyRate>(
      'currency/getCurrencyById'
    ),

    getModuleById: get<(id: number) => Module>(
      'configuratorModule/getModuleById'
    ),

    getQuoteServiceTemplateById: get<
      (id: number) => IQuoteServiceTemplate | undefined
    >('quoteTemplate/getQuoteServiceTemplateById'),

    airwayIcon: get('app/airwayIcon'),

    isQuoteVentilation(): boolean {
      return (
        this.quote.type == QuoteType.RTU_VENTILATION ||
        this.quote.type == QuoteType.AIRWAY_VENTILATION
      );
    },

    currency(): string | undefined {
      if (this.quote.currency) {
        if (
          this.getCurrencyById(this.quote.currency) &&
          this.getCurrencyById(this.quote.currency).currency
        ) {
          return (this.getCurrencyById(this.quote.currency)
            .currency as Currency)?.code;
        }
      }
      return 'CAD';
    },

    quoteCustomerPricing(): IQuoteCustomerPricing | null {
      if (this.quote.customer) {
        if (this.quote.customer.contract) {
          return this.quote.customer.contract!;
        }
      }
      return null;
    },

    snapshot(): number | null {
      if (this.quote.customer) {
        if (this.quote.customer.contract) {
          return this.quote.customer.contract.snapshot!;
        }
      }
      return null;
    },

    quoteId(): number {
      return Number(this.$route.params.quoteId);
    },

    quoteRevision(): number {
      return Number(this.$route.params.quoteRevision);
    },

    allowEwgDiscount(): boolean {
      if (this.quote.customer?.identifier) {
        return (
          this.getCustomerById(this.quote.customer.identifier)?.type === 'EWG'
        );
      }
      return false;
    },

    allowDiscounts(): boolean {
      return this.equipmentSubTotal > 0;
    },

    equipmentDiscountsTotal(): number {
      // TODO: plus item discount as well

      const reducer = (accumulator: number, currentQuote: number) =>
        accumulator + currentQuote;
      return (
        this.quote.categories
          .map(category => {
            const module = this.getModuleById(category.module);
            let discount = 0;
            if (!module.is_subscription) {
              discount = category.discount!;
            }
            return discount;
          })
          .reduce(reducer, 0) || 0
      );
    },

    equipmentSubTotal(): number {
      return (
        this.quote.categories.reduce((accumulator, category) => {
          let total = accumulator;
          const module = this.getModuleById(category.module);
          if (!module.is_subscription) {
            total += category.price ?? 0;
          }
          return total;
        }, 0) - this.equipmentDiscountsTotal
      );
    },

    ewgDiscountAmount(): number {
      return roundCurrency(
        (this.quote.ewg_discount_percent! / 100) * this.equipmentSubTotal
      );
    },

    specialDiscountAmount(): number {
      return roundCurrency(
        (this.quote.special_discount_percent! / 100) * this.equipmentSubTotal
      );
    },

    equipmentNetTotal(): number {
      return (
        this.equipmentSubTotal -
        this.specialDiscountAmount -
        this.ewgDiscountAmount
      );
    },

    amountTotal(): number {
      return [
        this.quote.service_items,
        this.quote.freight_items,
        this.quote.extra_items
      ]
        .flat()
        .map(item => Number(item.price! * this.quote.exchange_rate!))
        .reduce(
          (accumulator, cummalative) => accumulator + cummalative,
          this.equipmentNetTotal +
            this.subscriptionNetTotal +
            this.quote.surcharge
        );
    },

    oneTimeSubscriptionModuleQuoteCategories(): IQuoteProductCategory[] {
      return this.filterSubscriptionQuoteCategoriesByModulePaymentType(
        ModulePaymentTypeEnum.OneTime
      );
    },

    recurringSubscriptionModuleQuoteCategories(): IQuoteProductCategory[] {
      return this.filterSubscriptionQuoteCategoriesByModulePaymentType(
        ModulePaymentTypeEnum.Recurring
      );
    },

    subscriptionDiscountsTotal(): number {
      return this.subscriptionQuoteCategories.reduce(
        (accumulator, category) => {
          let total = accumulator;

          const discount = category.discount!;

          total += discount;

          return total;
        },
        0
      );
    },

    subscriptionFirstYearDiscountsTotal(): number {
      return this.subscriptionQuoteCategories.reduce(
        (accumulator, category) => {
          let discountTotal = accumulator;

          const netPrice = category.price! - category.discount!;

          const firstYearSubscriptionDiscount =
            (category.first_year_subscription_discount_percentage! * netPrice) /
            100;

          discountTotal += firstYearSubscriptionDiscount;

          return discountTotal;
        },
        0
      );
    },

    subscriptionSubTotal(): number {
      return (
        this.subscriptionQuoteCategories.reduce((accumulator, category) => {
          let total = accumulator;
          total += category.price ?? 0;

          return total;
        }, 0) - this.subscriptionDiscountsTotal
      );
    },

    subscriptionNetTotal(): number {
      return (
        this.subscriptionSubTotal - this.subscriptionFirstYearDiscountsTotal
      );
    },

    oneTimeSubscriptionFeeSubTotal(): number {
      return this.oneTimeSubscriptionModuleQuoteCategories.reduce(
        (accumulator, category) => {
          let total = accumulator;

          const netPrice = category.price! - category.discount!;

          total += netPrice!;

          return total;
        },
        0
      );
    },

    oneTimeSubscriptionFirstYearDiscountsTotal(): number {
      return this.oneTimeSubscriptionModuleQuoteCategories.reduce(
        (accumulator, category) => {
          let discountTotal = accumulator;

          const netPrice = category.price! - category.discount!;

          const firstYearSubscriptionDiscount =
            (category.first_year_subscription_discount_percentage! * netPrice) /
            100;

          discountTotal += firstYearSubscriptionDiscount;

          return discountTotal;
        },
        0
      );
    },

    oneTimeSubscriptionFeeNetTotal(): number {
      return (
        this.oneTimeSubscriptionFeeSubTotal -
        this.oneTimeSubscriptionFirstYearDiscountsTotal
      );
    },

    recurringSubscriptionFeeTotal(): number {
      return this.recurringSubscriptionModuleQuoteCategories.reduce(
        (accumulator, category) => {
          let total = accumulator;

          const netPrice = category.price! - category.discount!;

          total += netPrice!;

          return total;
        },
        0
      );
    },

    paymentRules() {
      //TODO: RC-2021-03-14 - Can we centralize this ruleset filtering?.
      return [
        (paymentTerms: QuotePaymentTermItem[]) =>
          this.checkPaymentTermSectionItems(paymentTerms)
      ];
    },

    priceSectionRules() {
      return [
        (section: QuoteFreightItem[] | QuoteServiceItem[] | QuoteExtraItem[]) =>
          this.checkPriceSectionItems(section)
      ];
    },

    termsRules() {
      return [(terms: QuoteTermItem[]) => this.checkTermSectionItems(terms)];
    }
  },

  async mounted() {
    await this.loadQuote();
  },

  methods: {
    async loadQuote() {
      this.setComponentState('loading');

      Promise.all([
        this.fetchCurrencies(),
        // Then is here as opposed to the end of the Promise.all is because we want to
        // continue as soon as the quote revision fetch is complete.
        // Instead of waiting for all of the request to be complete first.
        this.tryGetFetchQuote().then(() => {
          this.setComponentState('loaded');
        })
      ]).catch(error => {
        this.setComponentState('error', error);
      });
    },

    checkTermSectionItems(section: QuoteTermItem[]) {
      if (section.length) {
        const allHaveTitle = !cloneDeep(section).some(term => !term.title);
        if (allHaveTitle) {
          return true;
        } else {
          return 'All terms must have a title';
        }
      } else {
        return true;
      }
    },

    checkPaymentTermSectionItems(section: QuotePaymentTermItem[]) {
      if (section.length) {
        const allHaveTitle = !cloneDeep(section).some(
          paymentTerm => !paymentTerm.title
        );
        if (allHaveTitle) {
          return true;
        } else {
          return 'Payment must have a title';
        }
      }
      return true;
    },

    checkPriceSectionItems(
      section: QuoteFreightItem[] | QuoteServiceItem[] | QuoteExtraItem[]
    ) {
      if (section.length) {
        const allHaveTitle = !cloneDeep(section).some(item => !item.title);

        if (!allHaveTitle) {
          return 'Section must have a title';
        }
      }
      return true;
    },

    showSnackbarError(field: string) {
      this.createSnack({
        color: 'error',
        type: 'error',
        right: true,
        useBtn: false,
        msg: this.errors.get(field),
        btnText: 'Close',
        timeout: 6000,
        action: function() {}
      }).then(() => {
        this.errors.clear(field);
      });
    },

    ...mapActions({
      fetchModule: 'configuratorModule/fetchModule',

      fetchQuote: 'quote/fetchQuote',
      createQuote: 'quote/createQuote',
      updateQuote: 'quote/updateQuote',
      evaluateComputationPrice: 'quote/evaluateComputationPrice',
      evaluateComputationDays: 'quote/evaluateComputationDays',

      fetchQuoteServiceTemplate: 'quoteTemplate/fetchQuoteServiceTemplate',

      fetchCustomer: 'customer/fetchCustomer',

      fetchCurrencies: 'currency/fetchCurrencies'
    }),

    // TODO: LF-2021-08-30 - This should be replaced once we can get difference
    // and update only diff with the PATCH Request
    parseQuoteDataForEdit(quote: Quote) {
      // TODO: Check for any other fields that are serialized as string
      // ?For fields that are serialized as strings but need the id to be passed to the api
      const quoteClone = cloneDeep(quote);
      if (typeof quoteClone.agent === 'string') {
        delete quoteClone.agent;
      }
      if (typeof quoteClone.inside_sales_owner === 'string') {
        delete quoteClone.inside_sales_owner;
      }
      return quoteClone;
    },

    async saveForm() {
      this.clearAllErrors();

      // Set quote categories together
      this.quote.categories = this.subscriptionQuoteCategories.concat(
        this.equipmentQuoteCategories
      );

      if (await (this.$refs.quoteForm as any).validate()) {
        try {
          if (this.quoteId) {
            this.setComponentState('loading');
            await this.updateQuote(this.parseQuoteDataForEdit(this.quote))
              .then(() => {
                this.setComponentState('loaded');
              })
              .then(() => this.close());
          } else {
            // create
            this.setComponentState('loading');

            await this.createQuote(this.quote)
              .then(() => {
                this.setComponentState('loaded');
              })
              .then(() => this.close());
          }
        } catch (err) {
          const error = err as AxiosError;
          this.errors.extractErrorFromResponse(error.response!);
          // Should put component in loaded with errors shown
          if (this.errors.has('error')) {
            this.showSnackbarError('error');
          }
          this.setComponentState('loaded');

          if (error.response) {
            if (error.response.data.detail) {
              this.detailErrorMessage = error.response.data.detail;
            }
          }
          this.errorMessage = ErrorManager.extractApiError(error);
        }
      } else {
        this.errors.addErrorFromString('error', 'Error sending form request');
        if (this.errors.has('error')) {
          this.showSnackbarError('error');
        }
      }
    },

    async updateQuoteData(quote: Quote) {
      this.quote = quote;
      await this.setQuoteCategoryGroups(quote);
      this.originalQuote = cloneDeep(this.quote);
      if (quote.customer?.identifier) {
        this.fetchCustomer(quote.customer.identifier);
      }
    },

    async setQuoteCategoryGroups(quote: IQuote) {
      // TODO: LH - 2022-10-11 - Should look into to be optimized
      this.equipmentQuoteCategories = [];
      this.subscriptionQuoteCategories = [];
      for (let i = 0; i < quote.categories.length; i++) {
        const item = quote.categories[i];
        const module = await this.tryGetOrFetchModule(item.module);
        if (module.is_subscription) {
          this.subscriptionQuoteCategories.push(item);
        } else {
          this.equipmentQuoteCategories.push(item);
        }
      }
    },

    async tryGetOrFetchModule(id: number) {
      let module = this.getModuleById(id);
      if (!module) {
        module = await this.fetchModule(id);
      }
      return module;
    },

    clearAllErrors(): void {
      this.errorMessage = '';
      this.detailErrorMessage = '';
    },

    async isDaysComputationValid(serviceTemplateId: number): Promise<boolean> {
      const quoteServiceTemplate = await this.tryGetOrFetchQuoteServiceTemplate(
        serviceTemplateId
      );

      return quoteServiceTemplate.days_computations.length > 0;
    },

    async tryGetOrFetchQuoteServiceTemplate(
      serviceTemplateId: number
    ): Promise<IQuoteServiceTemplate> {
      const quoteServiceTemplate = this.getQuoteServiceTemplateById(
        serviceTemplateId
      );
      if (quoteServiceTemplate) {
        return quoteServiceTemplate;
      }
      await this.fetchQuoteServiceTemplate(serviceTemplateId).catch(error => {
        if (error.response) {
          if (error.response.data.detail) {
            this.detailErrorMessage = error.response.data.detail;
          }
        }
        this.errorMessage = ErrorManager.extractApiError(error);
      });
      return this.getQuoteServiceTemplateById(serviceTemplateId)!;
    },

    async quoteProductItemUpdated(quote: Quote) {
      await this.updateQuoteData(quote);
      this.loading = this.quote.service_items.length > 0;

      this.clearAllErrors();

      this.quote.service_items.forEach(async (service, serviceIndex) => {
        try {
          if (service.price_computation) {
            const price = await this.evaluateComputationPrice({
              id: this.quote.number,
              revision: this.quote.revision,
              computation: service.price_computation
            });

            if (await this.isDaysComputationValid(service.template!)) {
              const getFirstDayComputationId = (
                await this.tryGetOrFetchQuoteServiceTemplate(service.template!)
              ).days_computations[0].id;

              const days = await this.evaluateComputationDays({
                id: this.quote.number,
                revision: this.quote.revision,
                computation: getFirstDayComputationId
              });
              this.quote.service_items[serviceIndex].days = days;
            }

            this.quote.service_items[serviceIndex].price = price;
          }
        } catch (err) {
          const error = err as AxiosError;
          if (error.response) {
            if (error.response.data.detail) {
              this.detailErrorMessage = error.response.data.detail;
            }
          }
          this.errorMessage = ErrorManager.extractApiError(error);
        }
      });
      this.loading = false;
    },

    async tryGetFetchQuote() {
      if (this.quoteId) {
        let quote = this.getQuoteById(this.quoteId);
        if (!quote) {
          return this.fetchQuote(this.$route.params.quoteId).then(async () => {
            quote = this.getQuoteById(Number(this.$route.params.quoteId));
            await this.updateQuoteData(quote);
          });
        } else {
          await this.updateQuoteData(quote);
        }
      } else {
        return Promise.reject();
      }
    },

    momentFormat(date: Date | string | number, days = 0) {
      return moment(date ?? new Date())
        .add(days, 'd')
        .format('LL')
        .toString();
    },

    cancel() {
      if (!deepEqual(this.originalQuote, this.quote)) {
        this.discardUnsavedDataDialog = true;
      } else {
        this.close();
      }
    },

    closeUnsavedDataDialog() {
      this.discardUnsavedDataDialog = false;
    },

    close() {
      if (this.quoteId) {
        return this.$router.replace({
          name: 'quote-revision-detail',
          params: {
            quoteId: String(this.quoteId),
            revisionId: String(this.quoteRevision)
          }
        });
      } else {
        return this.$router.replace({
          name: 'quote-list'
        });
      }
    },

    async openQuoteWizardFormDialog() {
      const { default: wizardFormDialog } = await import(
        '@/components/quotes/QuoteWizardFormDialog.vue'
      );
      if (wizardFormDialog) {
        const formDialog = this.$refs.quoteWizardFormDialog as ExtractVue<
          typeof wizardFormDialog
        >;
        formDialog.open(this.quote);
      } else {
        this.createSnack({
          color: 'warning darken-1',
          type: 'warning',
          right: true,
          useBtn: false,
          msg: 'Quote wizard module failed to load',
          btnText: 'Close',
          timeout: 6000,
          action: function() {}
        });
      }
    },

    filterSubscriptionQuoteCategoriesByModulePaymentType(
      paymentType: ModulePaymentTypeEnum
    ): IQuoteProductCategory[] {
      return this.subscriptionQuoteCategories.filter(category => {
        const module = this.getModuleById(category.module);
        return module && module.payment_type == paymentType;
      });
    }
  }
});
